import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import Checkbox from '../../../../../App/Components/Checkbox/Checkbox'
import Dropdown from '../../../../../App/Components/Dropdown/Dropdown'
import TextInput from '../../../../../App/Components/Form/TextInput/TextInput'
import { useAppDispatch } from '../../../../../App/Hooks/reduxHooks'
import { Spinner } from '../../../../../Common/Spinner'
import { SurveyObject } from '../../../Client/ISurvey'
import {
  updateSurveyDescription,
  updateSurveyName,
  updateSurveyLanguage,
  updateSurveyMailSubject,
  deleteSurveyTrigger,
  createSurveyTriggerBeforeAppointment,
  createSurveyTriggerAfterAppointment,
} from '../../../Store/Action'
import style from '../cardStyle.scss'

import { TriggerAfter } from './components/TriggerAfter'
import { TriggerBefore } from './components/TriggerBefore'
import { Backdrop, SpinnerWrapper } from './style'

type Props = {
  survey: SurveyObject
  onFocus?: (id: string) => void
  isActive?: boolean
}

export type SurveyLanguage = 'nl' | 'fr' | 'en' | 'de'
export type TriggerFrequency = 'after_each_appointment' | 'only_once'
export type SurveyTriggerType =
  | 'send_after_appointment'
  | 'send_days_before_appointment'

type DescriptionHeading = {
  currentState: 'idle' | 'loading'
  title: string
  description: string
  mailSubject: string
  language: SurveyLanguage
}

export type TriggerAfterState = {
  uuid: string
  frequency: TriggerFrequency
  type: 'send_after_appointment'
  activityUuids: string[]
  categoryIds: number[]
}

export type TriggerBeforeState = {
  uuid: string
  frequency: TriggerFrequency
  type: 'send_days_before_appointment'
  amount: number
}

export type SurveyTrigger = TriggerAfterState | TriggerBeforeState

export type DescriptionCardState = DescriptionHeading & {
  trigger?: SurveyTrigger
}

const availableLanguages: SurveyLanguage[] = ['nl', 'fr', 'en', 'de']

export const EditDescriptionCard: React.FC<Props> = ({
  survey,
  onFocus,
  isActive,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { surveyId } = useParams<{ surveyId: string }>()

  const [descriptionCardState, setDescriptionCardState] =
    useState<DescriptionCardState>({
      currentState: 'loading',
      title: survey.name || '',
      description: survey.description || '',
      mailSubject: survey.mailSubject || '',
      language: survey.language,
    })

  const updateDescriptionCard = async (
    value: string,
    type: 'title' | 'description' | 'mailSubject' | 'language',
  ) => {
    if (value === descriptionCardState[type]) return

    // Start loader
    setDescriptionCardState({
      ...descriptionCardState,
      currentState: 'loading',
    })

    // Generic function to perform the update and handle success/failure
    const performUpdate = async (dispatchFunction: Promise<any>) => {
      try {
        await dispatchFunction
        // Handle success
        setDescriptionCardState({
          ...descriptionCardState,
          [type]: value,
          currentState: 'idle',
        })
      } catch (error) {
        // Handle failure
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'idle',
        })
      }
    }

    switch (type) {
      case 'title':
        await performUpdate(
          dispatch(updateSurveyName({ name: value, surveyId })),
        )
        break
      case 'description':
        await performUpdate(
          dispatch(updateSurveyDescription({ description: value, surveyId })),
        )
        break
      case 'mailSubject':
        await performUpdate(
          dispatch(updateSurveyMailSubject({ mailSubject: value, surveyId })),
        )
        break
      case 'language':
        await performUpdate(
          dispatch(updateSurveyLanguage({ language: value, surveyId })),
        )
        break
    }
  }

  const debouncedUpdate = useDebouncedCallback(
    (value: string, type: 'title' | 'description' | 'mailSubject') =>
      updateDescriptionCard(value, type),
    300,
  )

  const findTriggerType = () => {
    return survey.triggers.find(foundTrigger => {
      if (foundTrigger.type === 'send_after_appointment') {
        const triggerAfter: TriggerAfterState = {
          type: 'send_after_appointment',
          uuid: foundTrigger.uuid,
          frequency: foundTrigger.frequency,
          activityUuids: foundTrigger.activityUuids,
          categoryIds: foundTrigger.categoryIds,
        }
        return triggerAfter
      }
      const triggerBefore: TriggerBeforeState = {
        type: 'send_days_before_appointment',
        uuid: foundTrigger.uuid,
        frequency: foundTrigger.frequency,
        amount: foundTrigger.amount,
      }
      return triggerBefore
    })
  }

  const toggleTrigger = (triggerType: SurveyTriggerType) => {
    if (descriptionCardState.currentState === 'loading') return
    setDescriptionCardState({
      ...descriptionCardState,
      currentState: 'loading',
    })
    const currentTrigger = descriptionCardState.trigger
    if (!currentTrigger) {
      dispatch(
        triggerType === 'send_days_before_appointment'
          ? createSurveyTriggerBeforeAppointment(surveyId, triggerType)
          : createSurveyTriggerAfterAppointment(surveyId, triggerType),
      ).then(e => {
        const payload = e.payload
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'idle',
          trigger: {
            ...payload.trigger,
          },
        })
      })
    }
    if (currentTrigger?.type === triggerType) {
      dispatch(deleteSurveyTrigger(surveyId, currentTrigger.uuid)).then(() => {
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'idle',
          trigger: undefined,
        })
      })
    }
    if (currentTrigger && currentTrigger?.type !== triggerType) {
      dispatch(deleteSurveyTrigger(surveyId, currentTrigger.uuid)).then(() => {
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'loading',
          trigger: undefined,
        })
        dispatch(
          triggerType === 'send_days_before_appointment'
            ? createSurveyTriggerBeforeAppointment(surveyId, triggerType)
            : createSurveyTriggerAfterAppointment(surveyId, triggerType),
        ).then(e =>
          setDescriptionCardState({
            ...descriptionCardState,
            currentState: 'idle',
            trigger: {
              ...e.payload.trigger,
            },
          }),
        )
      })
    }
  }

  useEffect(() => {
    const trigger = findTriggerType()
    if (trigger) {
      setDescriptionCardState({
        ...descriptionCardState,
        trigger,
        currentState: 'idle',
      })
    } else {
      setDescriptionCardState({
        ...descriptionCardState,
        trigger: undefined,
        currentState: 'idle',
      })
    }
  }, [])

  return (
    <div
      className={`
                ${style.cardContainer}
                ${isActive ? style.cardContainerFocus : null}
            `}
      onFocus={() => onFocus && onFocus(surveyId)}>
      {descriptionCardState.currentState === 'loading' && (
        <SpinnerWrapper>
          <Spinner />
          <Backdrop />
        </SpinnerWrapper>
      )}
      <div className={style.cardHead}>
        <TextInput
          defaultValue={t(descriptionCardState.title)}
          onChange={titleInput =>
            debouncedUpdate(titleInput.target.value, 'title')
          }
          isRequired
          label={t('Survey title')}
          onBlur={() =>
            updateDescriptionCard(descriptionCardState.title, 'title')
          }
          onKeyPress={keyEvent =>
            keyEvent.key === 'Enter' ? keyEvent.currentTarget.blur() : true
          }
        />
      </div>

      <div className={style.cardBody}>
        <TextInput
          defaultValue={t(descriptionCardState.description)}
          onChange={descriptionInput =>
            debouncedUpdate(descriptionInput.target.value, 'description')
          }
          isRequired
          label={t('Survey description')}
          onBlur={() =>
            updateDescriptionCard(
              descriptionCardState.description,
              'description',
            )
          }
          onKeyPress={keyEvent =>
            keyEvent.key === 'Enter' ? keyEvent.currentTarget.blur() : true
          }
        />
      </div>
      <div className={style.cardBody}>
        <Dropdown
          label={t('Language')}
          value={descriptionCardState.language}
          onChange={e =>
            updateDescriptionCard(e.currentTarget.value, 'language')
          }
          isFullWidth>
          {availableLanguages.map(langOption => (
            <option key={langOption} value={langOption}>
              {t('locale.' + langOption)}
            </option>
          ))}
        </Dropdown>
      </div>
      <div className={style.cardBody}>
        <TextInput
          defaultValue={t(descriptionCardState.mailSubject)}
          onChange={mailSubjectInput =>
            debouncedUpdate(mailSubjectInput.target.value, 'mailSubject')
          }
          isRequired
          label={t('Mail Subject')}
          onBlur={() =>
            updateDescriptionCard(
              descriptionCardState.mailSubject,
              'mailSubject',
            )
          }
          onKeyPress={keyEvent =>
            keyEvent.key === '13' ? keyEvent.currentTarget.blur() : true
          }
        />
      </div>
      <div className={style.cardBody} style={{ flexDirection: 'column' }}>
        <Checkbox
          margin="15px 0"
          isChecked={
            descriptionCardState.trigger?.type === 'send_after_appointment'
          }
          onChange={e => toggleTrigger('send_after_appointment')}
          label={t('Send survey invitation after appointment is booked')}
        />
        <TriggerAfter
          descriptionCardState={descriptionCardState}
          setDescriptionCardState={setDescriptionCardState}
          surveyId={surveyId}
        />

        <Checkbox
          margin="15px 0"
          isChecked={
            descriptionCardState.trigger?.type ===
            'send_days_before_appointment'
          }
          onChange={e => toggleTrigger('send_days_before_appointment')}
          label={t('Send survey invitation before appointment')}
        />
        <TriggerBefore
          descriptionCardState={descriptionCardState}
          setDescriptionCardState={setDescriptionCardState}
          surveyId={surveyId}
        />
      </div>
    </div>
  )
}
